import {
  DeleteConfirmation,
  ExportDialog,
  Icon,
  SearchInput,
  ExportDropdown,
  Page,
  BackLink,
  IconButton,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
  TaskTypeIcon,
  Tooltip,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import styled from 'styled-components';
import { mimeTypes } from '~/utils';
import { ActiveStatusSelect } from '../custom-data/components';
import ProjectTaskTypeDrawer from './ProjectTaskTypeDrawer';

const TypeName = styled.span`
  display: flex;
  align-items: center;
`;

const DefaultTooltip = styled(Tooltip)`
  display: inline;
  margin-left: 0.5rem;
`;

function ProjectTaskTypesPage() {
  const documentTitle = useDocumentTitle('Task Types');

  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ q: '', isActive: 'true', size: 1000 });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projectTaskTypes()
        .get({ ...params, isActive: params.isActive ?? undefined });
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, params, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [dialog, setDialog] = useState(null);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onClose={resolve}
        onLoad={api.www
          .workspaces(workspace.id)
          .projectTaskTypes()
          .get(
            { ...params, isActive: params.isActive ?? undefined },
            { headers: { accept: mimeType }, responseType: 'blob' },
          )}
      />
    ));
  };

  const handleEdit = async (taskType) => {
    setDialog(
      <ProjectTaskTypeDrawer
        taskType={taskType}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Task Types');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  async function handleActiveStatusChange(item, flag) {
    try {
      await api.www.workspaces(workspace.id).projectTaskTypes(item.id).setActiveStatus(flag);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDefaultStatusChange(item) {
    try {
      await api.www.workspaces(workspace.id).projectTaskTypes(item.id).setDefaultStatus();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDelete(taskType) {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this Task Type?</DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .projectTaskTypes(taskType ? taskType.id : undefined)
        .delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Task Types</Page.Title>
          <Page.Description>
            Task types allow you to categorize tasks within projects. Standard types track time directly, while group
            types aggregate data from their child tasks.
          </Page.Description>
        </Page.Info>

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('task_types.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('task_types.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="plus" tooltip="New Task Type" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleFilterChange} />

          <ActiveStatusSelect value={params.isActive} onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column width="2.5rem" />
            <ListView.Column width="15rem">Name</ListView.Column>
            <ListView.Column>Mode</ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>
          <ListView.Body>
            {data.map((taskType) => {
              const { id, name, icon, color, mode, isActive, isDefault } = taskType;

              return (
                <ListView.Row onClick={() => handleEdit(taskType)} key={id} isDisabled={!isActive}>
                  <ListView.Cell>
                    <TaskTypeIcon icon={icon} color={color} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <TypeName>
                      {name}
                      {isDefault && (
                        <DefaultTooltip placement="right" message="The default task type for new tasks.">
                          <Icon icon="badge-check" />
                        </DefaultTooltip>
                      )}
                    </TypeName>
                  </ListView.Cell>
                  <ListView.Cell>{mode === 'standard' ? 'Standard' : 'Group'}</ListView.Cell>
                  <ListViewActions>
                    <>
                      <ListViewActions.Edit onClick={() => handleEdit(taskType)} />

                      <hr />

                      <ListViewMenu>
                        {({ setIsOpen }) => (
                          <>
                            <ListViewMenu.Item onClick={() => handleEdit(taskType)}>Edit</ListViewMenu.Item>
                            {!isDefault && (
                              <>
                                {isActive && mode === 'standard' && (
                                  <ListViewMenu.Item
                                    onClick={async () => {
                                      await handleDefaultStatusChange(taskType);
                                      setIsOpen(false);
                                    }}>
                                    Set as default
                                  </ListViewMenu.Item>
                                )}
                                <ListViewMenu.Item
                                  onClick={async () => {
                                    await handleActiveStatusChange(taskType, !isActive);
                                    setIsOpen(false);
                                  }}>
                                  {isActive ? 'Deactivate' : 'Activate'}
                                </ListViewMenu.Item>
                                <ListViewMenu.DeleteItem
                                  tooltip="This item is currently in use."
                                  onCheckDependencies={async (workspace) =>
                                    (await workspace.projectTaskTypes(id).hasDependencies()).data
                                  }
                                  onClick={() => handleDelete(taskType)}>
                                  Delete
                                </ListViewMenu.DeleteItem>
                              </>
                            )}
                          </>
                        )}
                      </ListViewMenu>
                    </>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>
          <ListView.Status total={data.length} label="Task Type" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}

export default ProjectTaskTypesPage;
